import type { ReactNode } from 'react';
import { PageHeader } from '../../../shared/components/PageHeader';
import { sectionTrail } from '../../../shared/components/section-trail';
import { BoardQuickStats } from '../components/BoardQuickStats';
import { BoardTripsTable } from '../components/BoardTripsTable';
import { FleetAvailabilityStrip } from '../components/FleetAvailabilityStrip';
import { JobQueueTable } from '../components/JobQueueTable';
import { useDispatchBoard } from '../hooks/use-dispatch-board';

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-fg">{title}</h2>
      {children}
    </section>
  );
}

/** The dispatcher's home (gitaumoses4/chukua#55): the jobs waiting for a crew, the trips on the road and the fleet that can take more. */
export function DispatchBoardPage() {
  const board = useDispatchBoard();

  return (
    <>
      <PageHeader
        breadcrumbs={sectionTrail('dispatch')}
        title="Dispatch"
        description="Jobs waiting for a crew, trips on the road, and the fleet available to take more."
        actions={<BoardQuickStats jobsQueued={board.jobsQueued} unitsAvailable={board.unitsAvailable} />}
      />
      <div className="flex flex-col gap-8">
        <FleetAvailabilityStrip state={board.fleet.state} onRetry={board.fleet.reload} />
        <Panel title="Job queue">
          <JobQueueTable
            state={board.queue.state}
            columns={board.columns}
            list={board.queue.list}
            isRefreshing={board.queue.isRefreshing}
            skippedCount={board.skipped.skippedIds.length}
            isShowingSkipped={board.skipped.isShowingSkipped}
            onToggleSkipped={() => board.skipped.setIsShowingSkipped(!board.skipped.isShowingSkipped)}
            onRetry={board.queue.reload}
          />
        </Panel>
        <Panel title="Trips on the road">
          <BoardTripsTable
            state={board.trips.state}
            canViewFleet={board.canViewFleet}
            canDispatch={board.canDispatch}
            onLoadMore={board.trips.loadMore}
            onRetry={board.trips.reload}
            onAborted={board.reloadAfterAbort}
          />
        </Panel>
      </div>
    </>
  );
}
